import React from 'react';
import { Select, Input, Button, Typography } from 'antd';
import type { Department } from '@shared/types';
import searchIcon from '../../assets/search-icon.svg';
import badgePlus from '../../assets/badge-plus.svg';
import styles from './FilterSection.module.css';

const { Option } = Select;
const { Title, Text } = Typography;

export interface PersonnelOption {
  id: string;
  name: string;
  code?: string;
}

export interface FilterSectionProps {
  headerTitle?: string;
  headerSubtitle?: string;
  onAddAction?: () => void;
  addButtonText?: string;
  title?: string;
  subtitle?: string;
  departments?: Department[];
  selectedDepartment?: string | null;
  onDepartmentChange?: (value: string) => void;
  searchPlaceholder?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  onSearch?: () => void;
  onClearFilter?: () => void;
  showDepartmentFilter?: boolean;
  showSearchInHeader?: boolean;
  customFilters?: React.ReactNode;
  extraActions?: React.ReactNode;
  headerActions?: React.ReactNode;
  showApplyButton?: boolean;
  applyButtonText?: string;
  onApply?: () => void;
  isFilterActive?: boolean;
  // Additional filters for timesheet table
  showPersonnelFilter?: boolean;
  personnelList?: PersonnelOption[];
  selectedPersonnel?: string | null;
  onPersonnelChange?: (value: string) => void;
  showMonthFilter?: boolean;
  selectedMonth?: number | null;
  onMonthChange?: (value: number) => void;
  showYearFilter?: boolean;
  selectedYear?: number | null;
  onYearChange?: (value: number) => void;
  // Status filter
  showStatusFilter?: boolean;
  selectedStatus?: boolean | null;
  onStatusChange?: (value: boolean | null) => void;
  // Custom text for "All" option in department filter
  allDepartmentOptionText?: string;
}

export const FilterSection: React.FC<FilterSectionProps> = ({
  headerTitle,
  headerSubtitle,
  onAddAction,
  addButtonText = 'Thêm nhân sự',
  title = 'Bộ lọc',
  subtitle = 'Tìm kiếm và lọc dữ liệu chấm công',
  departments = [],
  selectedDepartment,
  onDepartmentChange,
  searchPlaceholder = 'Tìm kiếm theo tên, mã NV, email, ...',
  searchValue,
  onSearchChange,
  onSearch,
  onClearFilter,
  showDepartmentFilter = true,
  showSearchInHeader = false,
  customFilters,
  extraActions,
  headerActions,
  showApplyButton = false,
  applyButtonText = 'Áp dụng',
  onApply,
  isFilterActive,
  showPersonnelFilter = false,
  personnelList = [],
  selectedPersonnel,
  onPersonnelChange,
  showMonthFilter = false,
  selectedMonth,
  onMonthChange,
  showYearFilter = false,
  selectedYear,
  onYearChange,
  showStatusFilter = false,
  selectedStatus,
  onStatusChange,
  allDepartmentOptionText = 'Tất cả',
}) => {
  // Kiểm tra xem có filter hoặc search đang active không
  // "Tất cả" không được coi là filter active
  const hasActiveFilter = typeof isFilterActive === 'boolean'
    ? isFilterActive
    : Boolean(
        (showDepartmentFilter &&
          selectedDepartment &&
          selectedDepartment.trim() !== '' &&
          selectedDepartment !== 'Tất cả' &&
          selectedDepartment !== allDepartmentOptionText) ||
        (showPersonnelFilter &&
          selectedPersonnel &&
          selectedPersonnel.trim() !== '' &&
          selectedPersonnel !== 'Tất cả') ||
        (showMonthFilter && selectedMonth !== null && selectedMonth !== undefined) ||
        (showYearFilter && selectedYear !== null && selectedYear !== undefined) ||
        (showStatusFilter && selectedStatus !== null && selectedStatus !== undefined) ||
        (searchValue && searchValue.trim() !== '')
      );

  return (
    <>
      {headerTitle && (
        <div className={styles.pageHeader}>
          <div>
            <Title level={2} className={styles.pageTitle}>
              {headerTitle}
            </Title>
            {headerSubtitle && (
              <Text type="secondary" className={styles.pageSubtitle}>
                {headerSubtitle}
              </Text>
            )}
          </div>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            {showSearchInHeader && (
              <Input
                placeholder={searchPlaceholder}
                prefix={<img src={searchIcon} alt="search" className={styles.searchIcon} />}
                value={searchValue}
                onChange={(e) => onSearchChange?.(e.target.value)}
                onPressEnter={onSearch}
                className={styles.searchInput}
                size="large"
                style={{ width: '300px' }}
              />
            )}
            {headerActions}
            {onAddAction && (
              <Button
                type="primary"
                icon={<img src={badgePlus} alt="add" className={styles.addIcon} />}
                size="large"
                onClick={onAddAction}
                className={styles.addButton}
              >
                {addButtonText}
              </Button>
            )}
          </div>
        </div>
      )}
      {(title || subtitle || showDepartmentFilter || showPersonnelFilter || showMonthFilter || showYearFilter || showStatusFilter || (searchValue !== undefined && !showSearchInHeader)) && (
        <div className={styles.filterSection}>
          {(title || subtitle) && (
            <div className={styles.filterHeader}>
              <div>
                {title && (
                  <Title level={2} className={styles.filterTitle}>
                    {title}
                  </Title>
                )}
                {subtitle && (
                  <Text type="secondary" className={styles.filterSubtitle}>
                    {subtitle}
                  </Text>
                )}
              </div>
              {(hasActiveFilter || showApplyButton || extraActions) && (
                <div className={styles.filterActions}>
                  {hasActiveFilter && onClearFilter && (
                    <Button
                      type="text"
                      onClick={onClearFilter}
                      className={styles.clearButton}
                    >
                      Xoá lọc
                    </Button>
                  )}
                  {extraActions}
                  {showApplyButton && (
                    <Button
                      type="primary"
                      onClick={onApply}
                      className={styles.applyButton}
                    >
                      {applyButtonText}
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className={styles.filterControls}>
            {customFilters ?? (
              <>
                <Input
                  placeholder={searchPlaceholder}
                  prefix={<img src={searchIcon} alt="search" className={styles.searchIcon} />}
                  value={searchValue}
                  onChange={(e) => onSearchChange?.(e.target.value)}
                  onPressEnter={onSearch}
                  className={styles.searchInput}
                  size="large"
                />

                {showDepartmentFilter && (
                  <Select
                    value={selectedDepartment}
                    onChange={onDepartmentChange}
                    className={styles.departmentSelect}
                    size="large"
                    placeholder="Phòng ban"
                  >
                    <Option value={allDepartmentOptionText}>{allDepartmentOptionText}</Option>
                    {departments.map((dept) => (
                      <Option key={dept.id} value={dept.name}>
                        {dept.name}
                      </Option>
                    ))}
                  </Select>
                )}

                {showPersonnelFilter && (
                  <Select
                    value={selectedPersonnel}
                    onChange={onPersonnelChange}
                    className={styles.filterSelect}
                    size="large"
                    placeholder="Nhân sự"
                  >
                    <Option value="Tất cả">Tất cả</Option>
                    {personnelList.map((person) => (
                      <Option key={person.id} value={person.id}>
                        {person.name} {person.code ? `(${person.code})` : ''}
                      </Option>
                    ))}
                  </Select>
                )}

                {showMonthFilter && (
                  <Select
                    value={selectedMonth}
                    onChange={onMonthChange}
                    className={styles.filterSelect}
                    size="large"
                    placeholder="Tháng"
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                      <Option key={month} value={month}>
                        Tháng {month}
                      </Option>
                    ))}
                  </Select>
                )}

                {showYearFilter && (
                  <Select
                    value={selectedYear}
                    onChange={onYearChange}
                    className={styles.filterSelect}
                    size="large"
                    placeholder="Năm"
                  >
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                      <Option key={year} value={year}>
                        Năm {year}
                      </Option>
                    ))}
                  </Select>
                )}

                {showStatusFilter && (
                  <Select
                    value={selectedStatus === null || selectedStatus === undefined ? null : selectedStatus ? 'active' : 'inactive'}
                    onChange={(value) => {
                      if (value === null || value === 'all') {
                        onStatusChange?.(null);
                      } else {
                        onStatusChange?.(value === 'active');
                      }
                    }}
                    className={styles.filterSelect}
                    size="large"
                    placeholder="Trạng thái"
                  >
                    <Option value="all">Tất cả</Option>
                    <Option value="active">Hoạt động</Option>
                    <Option value="inactive">Không hoạt động</Option>
                  </Select>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

